import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Loader2, ShieldCheck, Users, LifeBuoy, Crown, UserPlus,
  CreditCard, BarChart3, X, Send, CheckCircle2, RotateCcw, Star, Clock,
  UserCog, StickyNote, Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { appApi } from "@/lib/appApi";
import { useAuth } from "@/context/AuthContext";
import { planFromKey } from "@/data/site";

export const isOwner = (u) => !!(u && (u.is_super_root || u.is_super_admin || u.is_admin));

const arr = (d, keys) => {
  for (const k of keys) if (Array.isArray(d?.[k])) return d[k];
  return Array.isArray(d) ? d : [];
};
const num = (v) => (v == null || isNaN(Number(v)) ? null : Number(v));
// This backend uses Stripe minor units (cents) for monetary amounts.
const money = (v, cur = "usd") => {
  const n = num(v);
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: (cur || "usd").toUpperCase() }).format(n / 100);
  } catch {
    return `$${(n / 100).toFixed(2)}`;
  }
};
const when = (v) => {
  if (!v) return "";
  const d = new Date(typeof v === "number" ? (v < 1e12 ? v * 1000 : v) : v);
  return isNaN(d) ? String(v) : d.toLocaleString();
};
const planName = (k) => (k ? (planFromKey?.(k)?.name || String(k).replace(/_/g, " ")) : "free");

export default function Console() {
  const { user, loading, token, openAuth } = useAuth();
  const [tab, setTab] = useState("support");
  const [data, setData] = useState({
    support: [], users: [], admins: [], purchases: [], planBreakdown: null, analytics: null, ratings: null, assignees: [],
  });
  const [busy, setBusy] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [active, setActive] = useState(null); // open ticket thread
  const [supportFilter, setSupportFilter] = useState("all"); // all | open | resolved | unassigned
  const [supportQuery, setSupportQuery] = useState("");

  const owner = isOwner(user);

  const fetchAll = useCallback(async () => {
    if (!owner || !token) return;
    setBusy(true);
    const safe = async (p, keys) => { try { return arr(await appApi(p, { token }), keys); } catch { return []; } };
    const raw = async (p) => { try { return await appApi(p, { token }); } catch { return null; } };
    const [support, users, admins, purchases, planBreakdown, analytics, ratings, assignees] = await Promise.all([
      safe("/api/platform/support/threads", ["threads", "items", "results"]),
      safe("/api/platform/users", ["users", "items", "results"]),
      safe("/api/platform/admins", ["admins", "items", "results"]),
      safe("/api/platform/purchases?limit=100", ["purchases", "items", "results"]),
      raw("/api/platform/plan-breakdown"),
      raw("/api/platform/support/analytics"),
      raw("/api/platform/support/ratings"),
      safe("/api/platform/support/assignees", ["assignees", "agents", "items", "results"]),
    ]);
    setData({ support, users, admins, purchases, planBreakdown, analytics, ratings, assignees });
    setBusy(false);
  }, [owner, token]);

  useEffect(() => { document.title = "Owner Console — Warranty_Vault pro"; }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredSupport = useMemo(() => {
    const q = supportQuery.trim().toLowerCase();
    return data.support.filter((t) => {
      const status = String(t.status || "open").toLowerCase();
      const unassigned = !(t.assignee_id || t.assigned_to || t.assignee?.id);
      if (supportFilter === "open" && (status === "resolved" || status === "closed")) return false;
      if (supportFilter === "resolved" && !(status === "resolved" || status === "closed")) return false;
      if (supportFilter === "unassigned" && !unassigned) return false;
      if (q) {
        const hay = `${t.subject || ""} ${t.user_name || ""} ${t.user_email || ""} ${t.user || ""} ${t.last_message || t.preview || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data.support, supportFilter, supportQuery]);

  const unreadCount = useMemo(() => {
    const signalKeys = ["unread", "has_unread", "unanswered", "awaiting_reply", "needs_reply", "unread_count", "last_message_sender", "last_sender", "last_message_from"];
    const hasSignal = data.support.some((t) => signalKeys.some((k) => k in (t || {})));
    const needsAttention = (t) => {
      const status = String(t.status || "open").toLowerCase();
      if (status === "resolved" || status === "closed") return false;
      if (!hasSignal) return true; // backend gives no read/answered signal -> count every open ticket
      if (t.unread === true || t.has_unread === true || t.unanswered === true || t.awaiting_reply === true || t.needs_reply === true) return true;
      if (typeof t.unread_count === "number" && t.unread_count > 0) return true;
      const lastFrom = String(t.last_message_sender || t.last_sender || t.last_message_from || "").toLowerCase();
      if (lastFrom) return /user|customer|client/.test(lastFrom);
      return false;
    };
    return data.support.filter(needsAttention).length;
  }, [data.support]);

  if (loading) return <main className="min-h-screen pt-28" data-testid="console-loading" />;

  if (!user) {
    return (
      <main className="min-h-screen pt-28" data-testid="console-guest">
        <div className="mx-auto max-w-xl px-5 py-16 text-center sm:px-8">
          <h1 className="font-display text-3xl font-bold">Owner Console</h1>
          <p className="mt-3 text-muted-foreground">Sign in with your owner account to continue.</p>
          <button onClick={() => openAuth("login")} className="mt-6 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Log in</button>
        </div>
      </main>
    );
  }

  if (!owner) {
    return (
      <main className="min-h-screen pt-28" data-testid="console-forbidden">
        <div className="mx-auto max-w-xl px-5 py-16 text-center sm:px-8">
          <h1 className="font-display text-3xl font-bold">Not authorised</h1>
          <p className="mt-3 text-muted-foreground">This console is only available to owner/admin accounts.</p>
          <Link to="/" className="mt-6 inline-block rounded-full border border-border px-6 py-3 text-sm font-semibold">Back home</Link>
        </div>
      </main>
    );
  }

  const grant = async (revoke = false) => {
    const email = grantEmail.trim();
    if (!email) return;
    try {
      await appApi(`/api/platform/admins/${revoke ? "revoke" : "grant"}`, { method: "POST", token, body: { email } });
      setGrantEmail("");
      fetchAll();
    } catch (e) { alert(e.message || "Action failed"); }
  };

  const openThread = async (id) => {
    if (!id) return;
    setActive({ id, loading: true, data: null });
    try {
      const d = await appApi(`/api/platform/support/threads/${id}`, { token });
      setActive({ id, loading: false, data: d });
    } catch (e) {
      setActive({ id, loading: false, data: null, error: e.message });
    }
  };

  const threadAction = async (action, body) => {
    if (!active?.id) return;
    await appApi(`/api/platform/support/threads/${active.id}/${action}`, { method: "POST", token, body });
  };

  const TABS = [
    { k: "support", label: "Support", icon: LifeBuoy, count: data.support.length, badge: unreadCount },
    { k: "purchases", label: "Purchases", icon: CreditCard, count: data.purchases.length },
    { k: "analytics", label: "Analytics", icon: BarChart3 },
    { k: "users", label: "Users", icon: Users, count: data.users.length },
    { k: "admins", label: "Admins", icon: Crown, count: data.admins.length },
  ];

  return (
    <main className="relative min-h-screen pt-28" data-testid="console-page">
      <div className="vault-grid-bg grain absolute inset-x-0 top-0 h-72" />
      <div className="relative mx-auto max-w-5xl px-5 pb-20 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold"><ArrowLeft className="h-4 w-4" /> Home</Link>
        <Reveal className="mt-4">
          <p className="eyebrow inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Owner Console</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight">Welcome, {user.name || "Owner"}</h1>
          <p className="mt-2 text-muted-foreground">Support, purchases, analytics, users and admin roles — live from your app backend.</p>
        </Reveal>

        <div className="mt-8 flex flex-wrap gap-2 rounded-2xl border border-border bg-card/60 p-1.5">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`console-${t.k}-tab`}
              className={`relative inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="h-4 w-4" /> {t.label}{t.count != null && <span className="opacity-70">({t.count})</span>}
              {t.badge > 0 && (
                <span data-testid="support-unread-badge" className="ml-0.5 inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">
                  {t.badge > 99 ? "99+" : t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {busy && <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}

        {!busy && tab === "support" && (
          <div className="mt-6" data-testid="console-support">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="inline-flex rounded-full border border-border bg-card/60 p-1">
                {[
                  { k: "all", label: "All" },
                  { k: "open", label: "Open" },
                  { k: "resolved", label: "Resolved" },
                  { k: "unassigned", label: "Unassigned" },
                ].map((f) => (
                  <button key={f.k} onClick={() => setSupportFilter(f.k)} data-testid={`support-filter-${f.k}`}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${supportFilter === f.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative sm:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input value={supportQuery} onChange={(e) => setSupportQuery(e.target.value)} placeholder="Search tickets…" data-testid="support-search"
                  className="w-full rounded-full border border-border bg-card px-9 py-2 text-sm outline-none focus:border-gold/40" />
                {supportQuery && (
                  <button onClick={() => setSupportQuery("")} data-testid="support-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </div>

            <div className="space-y-3" data-testid="console-support-list">
              {filteredSupport.length === 0 && <Empty label={data.support.length ? "No tickets match your filters." : "No support threads found."} />}
              {filteredSupport.map((t, i) => {
                const id = t.id || t.thread_id || t._id;
                const unassigned = !(t.assignee_id || t.assigned_to || t.assignee?.id);
                return (
                  <button key={id || i} onClick={() => openThread(id)} data-testid={`support-thread-${i}`}
                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-card/40 p-4 text-left transition-colors hover:border-gold/40">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{t.subject || t.user_name || t.user_email || t.user || `Thread ${id || i + 1}`}</p>
                      <p className="mt-1 truncate text-sm text-muted-foreground">{t.last_message || t.preview || t.user_email || ""}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs capitalize text-accent-foreground">{t.status || "open"}</span>
                      {unassigned && <span className="text-[10px] font-semibold uppercase tracking-wide text-gold">Unassigned</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!busy && tab === "purchases" && <PurchasesTab purchases={data.purchases} breakdown={data.planBreakdown} />}

        {!busy && tab === "analytics" && <AnalyticsTab analytics={data.analytics} ratings={data.ratings} />}

        {!busy && tab === "users" && (
          <div className="mt-6 overflow-x-auto rounded-xl border border-border" data-testid="console-users-list">
            {data.users.length === 0 ? <Empty label="No users returned." /> : (
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="bg-card/60"><tr>{["Name", "Email", "Plan"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {data.users.slice(0, 100).map((u, i) => (
                    <tr key={u.id || i} className="border-t border-border">
                      <td className="px-4 py-3">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3 capitalize">{planName(u.plan_key) !== "free" ? planName(u.plan_key) : (u.plan_type || u.plan || "free")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!busy && tab === "admins" && (
          <div className="mt-6" data-testid="console-admins">
            <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card/40 p-4">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Grant / revoke admin by email</label>
                <input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="person@example.com" data-testid="admin-grant-email"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-gold/40" />
              </div>
              <button onClick={() => grant(false)} data-testid="admin-grant-btn" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><UserPlus className="h-4 w-4" /> Grant</button>
              <button onClick={() => grant(true)} data-testid="admin-revoke-btn" className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-destructive">Revoke</button>
            </div>
            <div className="mt-4 space-y-2">
              {data.admins.length === 0 ? <Empty label="No admins returned." /> : data.admins.map((a, i) => (
                <div key={a.id || a.email || i} className="flex items-center justify-between rounded-xl border border-border bg-card/40 px-4 py-3 text-sm">
                  <span className="font-medium">{a.name || a.email}</span>
                  <span className="text-xs text-muted-foreground">{a.email}{a.is_super_root ? " · super" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {active && (
        <ThreadDrawer
          active={active}
          assignees={data.assignees}
          onClose={() => setActive(null)}
          onReply={async (text) => { await threadAction("reply", { text }); await openThread(active.id); fetchAll(); }}
          onResolve={async () => { await threadAction("resolve"); await openThread(active.id); fetchAll(); }}
          onReopen={async () => { await threadAction("reopen"); await openThread(active.id); fetchAll(); }}
          onAssign={async (assignee_id) => { await threadAction("assign", { assignee_id }); await openThread(active.id); fetchAll(); }}
          onNote={async (text) => { await threadAction("note", { text }); await openThread(active.id); }}
        />
      )}
    </main>
  );
}

/* ---------- Purchases & Revenue ---------- */
function PurchasesTab({ purchases, breakdown }) {
  const amountOf = (p) => num(p.amount_total ?? p.amount ?? p.total ?? p.amount_paid ?? p.price);
  const totalRevenue = purchases.reduce((s, p) => s + (amountOf(p) || 0), 0);
  const currency = purchases.find((p) => p.currency)?.currency || "usd";

  // monthly revenue series (range-selectable)
  const [range, setRange] = useState(12); // 6 | 12 | all
  const monthlyAll = useMemo(() => {
    const map = new Map();
    purchases.forEach((p) => {
      const raw = p.created_at || p.date || p.created;
      if (!raw) return;
      const d = new Date(typeof raw === "number" ? (raw < 1e12 ? raw * 1000 : raw) : raw);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      map.set(key, (map.get(key) || 0) + (amountOf(p) || 0));
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, v]) => ({ key: k, label: new Date(`${k}-01`).toLocaleDateString("en-US", { month: "short", year: "2-digit" }), value: v }));
  }, [purchases]); // eslint-disable-line react-hooks/exhaustive-deps
  const monthly = range === "all" ? monthlyAll : monthlyAll.slice(-range);
  const maxMonth = Math.max(1, ...monthly.map((m) => m.value));

  // plan breakdown: accept array or object map
  const rows = useMemo(() => {
    if (!breakdown) return [];
    const src = breakdown.breakdown || breakdown.plans || breakdown.items || breakdown.results || breakdown;
    if (Array.isArray(src)) {
      return src.map((r) => ({
        plan: r.plan_key || r.plan || r.key || r.name,
        count: num(r.count ?? r.users ?? r.total ?? r.qty),
        revenue: num(r.revenue ?? r.amount ?? r.total_revenue),
      }));
    }
    if (src && typeof src === "object") {
      return Object.entries(src).map(([k, v]) => ({
        plan: k,
        count: typeof v === "object" ? num(v.count ?? v.users ?? v.total) : num(v),
        revenue: typeof v === "object" ? num(v.revenue ?? v.amount) : null,
      }));
    }
    return [];
  }, [breakdown]);

  return (
    <div className="mt-6 space-y-8" data-testid="console-purchases">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total revenue" value={money(totalRevenue, currency)} />
        <StatCard label="Purchases" value={purchases.length} />
        <StatCard label="Plans tracked" value={rows.length || "—"} />
      </div>

      {monthlyAll.length > 0 && (
        <div data-testid="console-revenue-chart">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold">Monthly revenue</h2>
            <div className="inline-flex rounded-full border border-border bg-card/60 p-1 text-xs">
              {[{ k: 6, label: "6M" }, { k: 12, label: "12M" }, { k: "all", label: "All" }].map((r) => (
                <button key={r.k} onClick={() => setRange(r.k)} data-testid={`revenue-range-${r.k}`}
                  className={`rounded-full px-3 py-1 font-semibold transition-colors ${range === r.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card/40 p-5">
            <div className="flex h-48 items-end gap-2 sm:gap-3">
              {monthly.map((m) => (
                <div key={m.key} className="flex flex-1 flex-col items-center gap-2">
                  <span className="text-[10px] font-semibold text-gold">{m.value > 0 ? money(m.value, currency) : ""}</span>
                  <div className="flex w-full items-end" style={{ height: "100%" }}>
                    <div
                      className="w-full rounded-t-md bg-gradient-to-t from-primary to-gold transition-all"
                      style={{ height: `${Math.max(4, (m.value / maxMonth) * 100)}%` }}
                      title={`${m.label}: ${money(m.value, currency)}`}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div>
          <h2 className="mb-3 font-display text-lg font-bold">Plan breakdown</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {rows.map((r, i) => (
              <div key={r.plan || i} className="flex items-center justify-between rounded-xl border border-border bg-card/40 px-4 py-3">
                <span className="font-semibold capitalize">{planName(r.plan)}</span>
                <span className="text-sm text-muted-foreground">
                  {r.count != null && <>{r.count} {r.count === 1 ? "user" : "users"}</>}
                  {r.revenue != null && <span className="ml-2 text-gold">{money(r.revenue, currency)}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-3 font-display text-lg font-bold">Recent purchases</h2>
        {purchases.length === 0 ? <Empty label="No purchases found." /> : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="bg-card/60"><tr>{["Customer", "Plan", "Amount", "Status", "Date"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {purchases.slice(0, 100).map((p, i) => (
                  <tr key={p.id || i} className="border-t border-border">
                    <td className="px-4 py-3">{p.user_email || p.email || p.user_name || p.user || "—"}</td>
                    <td className="px-4 py-3 capitalize">{planName(p.plan_key || p.plan || p.plan_type)}</td>
                    <td className="px-4 py-3 text-gold">{money(amountOf(p), p.currency || currency)}</td>
                    <td className="px-4 py-3 capitalize text-muted-foreground">{p.status || "paid"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{when(p.created_at || p.date || p.created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Analytics & Ratings ---------- */
function AnalyticsTab({ analytics, ratings }) {
  const metrics = useMemo(() => {
    const src = analytics?.analytics || analytics?.stats || analytics || {};
    return Object.entries(src).filter(([, v]) => typeof v === "number" || typeof v === "string");
  }, [analytics]);

  const list = arr(ratings, ["ratings", "items", "results"]);
  const avg = num(ratings?.average ?? ratings?.avg ?? ratings?.average_rating);
  const computedAvg = avg != null ? avg : (list.length ? list.reduce((s, r) => s + (num(r.rating ?? r.stars ?? r.score) || 0), 0) / list.length : null);

  return (
    <div className="mt-6 space-y-8" data-testid="console-analytics">
      {metrics.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map(([k, v]) => <StatCard key={k} label={k.replace(/_/g, " ")} value={typeof v === "number" ? v.toLocaleString() : v} />)}
        </div>
      ) : <Empty label="No support analytics returned." />}

      <div>
        <div className="mb-3 flex items-center gap-3">
          <h2 className="font-display text-lg font-bold">Ratings</h2>
          {computedAvg != null && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-3 py-1 text-sm font-semibold text-gold">
              <Star className="h-4 w-4 fill-gold" /> {computedAvg.toFixed(1)}
            </span>
          )}
        </div>
        {list.length === 0 ? <Empty label="No ratings yet." /> : (
          <div className="space-y-2">
            {list.slice(0, 50).map((r, i) => {
              const stars = num(r.rating ?? r.stars ?? r.score) || 0;
              return (
                <div key={r.id || i} className="rounded-xl border border-border bg-card/40 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className={`h-4 w-4 ${s < stars ? "fill-gold text-gold" : "text-muted-foreground/40"}`} />
                      ))}
                    </span>
                    <span className="text-xs text-muted-foreground">{r.user_email || r.user || ""} · {when(r.created_at || r.date)}</span>
                  </div>
                  {(r.comment || r.feedback || r.text) && <p className="mt-2 text-sm text-muted-foreground">{r.comment || r.feedback || r.text}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Thread drawer (view + reply + assign + notes) ---------- */
function ThreadDrawer({ active, assignees = [], onClose, onReply, onResolve, onReopen, onAssign, onNote }) {
  const [text, setText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [view, setView] = useState("chat"); // chat | notes
  const [sending, setSending] = useState(false);
  const [acting, setActing] = useState(false);
  const bottomRef = useRef(null);
  const t = active.data || {};
  const thread = t.thread || t;
  const messages = arr(thread, ["messages", "history", "items"]);
  const notes = arr(thread, ["notes", "internal_notes"]);
  const status = thread.status || "open";
  const subject = thread.subject || thread.user_name || thread.user_email || `Thread ${active.id}`;
  const currentAssignee = thread.assignee_id || thread.assigned_to || thread.assignee?.id || "";

  const agentId = (a) => a.id || a.user_id || a.assignee_id || a._id || a.email;
  const agentName = (a) => a.name || a.full_name || a.email || agentId(a);

  useEffect(() => { if (view === "chat") bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, view]);

  const send = async () => {
    const v = text.trim();
    if (!v || sending) return;
    setSending(true);
    try { await onReply(v); setText(""); } catch (e) { alert(e.message || "Reply failed"); } finally { setSending(false); }
  };
  const addNote = async () => {
    const v = noteText.trim();
    if (!v || sending) return;
    setSending(true);
    try { await onNote(v); setNoteText(""); } catch (e) { alert(e.message || "Note failed"); } finally { setSending(false); }
  };
  const doAction = async (fn) => { setActing(true); try { await fn(); } catch (e) { alert(e.message || "Action failed"); } finally { setActing(false); } };

  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="thread-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <aside className="flex h-full w-full max-w-md flex-col border-l border-border bg-background shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-bold">{subject}</p>
            <span className="text-xs capitalize text-muted-foreground">{status}</span>
          </div>
          <button onClick={onClose} data-testid="thread-close" className="grid h-8 w-8 place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        {/* assignee + view toggle */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><UserCog className="h-3.5 w-3.5" /> Agent</span>
          <select
            value={currentAssignee}
            onChange={(e) => doAction(() => onAssign(e.target.value || null))}
            disabled={acting}
            data-testid="thread-assignee"
            className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs outline-none focus:border-gold/40 disabled:opacity-50"
          >
            <option value="">Unassigned</option>
            {assignees.map((a) => <option key={agentId(a)} value={agentId(a)}>{agentName(a)}</option>)}
          </select>
          <div className="ml-auto inline-flex rounded-full border border-border p-0.5 text-xs">
            <button onClick={() => setView("chat")} data-testid="thread-view-chat" className={`rounded-full px-3 py-1 font-semibold ${view === "chat" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Conversation</button>
            <button onClick={() => setView("notes")} data-testid="thread-view-notes" className={`rounded-full px-3 py-1 font-semibold ${view === "notes" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Notes{notes.length ? ` (${notes.length})` : ""}</button>
          </div>
        </div>

        {view === "chat" ? (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4" data-testid="thread-messages">
              {active.loading && <div className="flex justify-center pt-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
              {!active.loading && messages.length === 0 && <p className="pt-10 text-center text-sm text-muted-foreground">No messages in this thread.</p>}
              {messages.map((m, i) => {
                const body = m.text || m.body || m.message || m.content || "";
                const who = String(m.sender || m.role || m.author || m.from || (m.is_admin ? "admin" : "user")).toLowerCase();
                const admin = m.is_admin === true || /admin|agent|support|owner|vera|staff/.test(who);
                return (
                  <div key={m.id || i} className={`flex ${admin ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${admin ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                      <p className="whitespace-pre-wrap">{body}</p>
                      <p className={`mt-1 text-[10px] ${admin ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        <Clock className="mr-1 inline h-3 w-3" />{when(m.created_at || m.timestamp || m.date)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            <div className="border-t border-border px-5 py-3">
              <div className="mb-2 flex gap-2">
                {status !== "resolved" && status !== "closed" ? (
                  <button onClick={() => doAction(onResolve)} disabled={acting} data-testid="thread-resolve"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-emerald-500 disabled:opacity-50">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Resolve
                  </button>
                ) : (
                  <button onClick={() => doAction(onReopen)} disabled={acting} data-testid="thread-reopen"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground disabled:opacity-50">
                    <RotateCcw className="h-3.5 w-3.5" /> Reopen
                  </button>
                )}
              </div>
              <div className="flex items-end gap-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Type a reply…" data-testid="thread-reply-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                  className="flex-1 resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-gold/40" />
                <button onClick={send} disabled={sending || !text.trim()} data-testid="thread-reply-send"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground disabled:opacity-50">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4" data-testid="thread-notes">
              <p className="text-xs text-muted-foreground">Internal notes are only visible to your team — the customer never sees them.</p>
              {notes.length === 0 && <p className="pt-6 text-center text-sm text-muted-foreground">No internal notes yet.</p>}
              {notes.map((n, i) => (
                <div key={n.id || i} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
                  <p className="whitespace-pre-wrap">{n.text || n.body || n.message || ""}</p>
                  <p className="mt-1 text-[10px] text-muted-foreground"><StickyNote className="mr-1 inline h-3 w-3" />{n.author || n.by || "Team"} · {when(n.created_at || n.date)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-border px-5 py-3">
              <div className="flex items-end gap-2">
                <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Add an internal note…" data-testid="thread-note-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
                  className="flex-1 resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-amber-500/40" />
                <button onClick={addNote} disabled={sending || !noteText.trim()} data-testid="thread-note-send"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-500 text-black disabled:opacity-50">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <StickyNote className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl border border-border bg-card/40 p-5">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-2 font-display text-2xl font-extrabold">{value}</p>
  </div>
);

const Empty = ({ label }) => <p className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">{label}</p>;
